import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShoppingCart } from 'lucide-react';
import { addToCart } from '../utils/cartUtils';
import { toast } from 'sonner';
import { getProducts, getCategories } from '../services/api';

const Shop = () => {
  const [searchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [loading, setLoading] = useState(true);

  // Load data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, categoriesData] = await Promise.all([
          getProducts(),
          getCategories()
        ]);
        setAllProducts(productsData);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter and sort products
  useEffect(() => {
    const category = searchParams.get('category');
    const search = searchParams.get('search');
    
    if (category) {
      setSelectedCategory(category);
    }

    let filtered = [...allProducts];

    // Filter by category
    if (category && category !== 'all') {
      filtered = filtered.filter(p => p.category === category);
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower) ||
        p.category.toLowerCase().includes(searchLower)
      );
    }

    // Sort
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      // Featured first
      filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    setFilteredProducts(filtered);
  }, [searchParams, selectedCategory, sortBy, allProducts]);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading products...</p>
      </div>
    );
  }

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    addToCart(product, 1);
    toast.success(`${product.name} added to cart!`);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    window.history.pushState({}, '', category === 'all' ? '/shop' : `/shop?category=${category}`);
  };

  return (
    <div className="shop-page">
      <div className="shop-header">
        <h1 className="shop-title">Shop Natural Wellness</h1>
        <p className="shop-subtitle">Discover our full collection of organic and holistic products</p>
      </div>

      <div className="shop-container">
        {/* Filters Sidebar */}
        <aside className="shop-sidebar">
          <div className="filter-section">
            <h3 className="filter-title">Categories</h3>
            <div className="category-filters">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`category-filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              >
                All Products
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`category-filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h3 className="filter-title">Sort By</h3>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="name">Name: A to Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </aside>

        {/* Products Grid */}
        <div className="shop-main">
          <div className="shop-results-header">
            <p className="results-count">{filteredProducts.length} products found</p>
          </div>

          {filteredProducts.length === 0 ? (
            <div className="no-results">
              <p>No products found matching your criteria.</p>
              <Button asChild className="mt-4">
                <Link to="/shop">View All Products</Link>
              </Button>
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map(product => (
                <Card key={product.id} className="product-card">
                  <Link to={`/product/${product.id}`}>
                    <div className="product-image-wrapper">
                      <img src={product.image} alt={product.name} className="product-image" />
                      {product.featured && (
                        <span className="product-badge">Featured</span>
                      )}
                    </div>
                    <div className="product-info">
                      <p className="product-category">{product.category}</p>
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-description-short">{product.description.substring(0, 80)}...</p>
                      <div className="product-footer">
                        <p className="product-price">${product.price.toFixed(2)}</p>
                        <Button
                          size="sm"
                          onClick={(e) => handleAddToCart(product, e)}
                          className="add-to-cart-btn"
                        >
                          <ShoppingCart size={16} className="mr-1" /> Add
                        </Button>
                      </div>
                    </div>
                  </Link>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
